import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { AccessFunctionRequest } from '../../authorize/models/access-function-request';
import { AchInquiryRequest } from '../../create-request/models/ach/AchInquiryRequest';
import { AchInquiryResponse } from '../../create-request/models/ach/AchInquiryResponse';
import { CheckInquiryRequest } from '../../create-request/models/check/CheckInquiryRequest';
import { CheckInquiryResponse } from '../../create-request/models/check/CheckInquiryResponse';
import { InternalTransferInquiryRequest } from '../../create-request/models/internal-transfer/InternalTransferInquiryRequest';
import { InternalTransferInquiryResponse } from '../../create-request/models/internal-transfer/InternalTransferInquiryResponse';
import { WireInquiryRequest } from '../../create-request/models/wire/WireInquiryRequest';
import { WireInquiryResponse } from '../../create-request/models/wire/WireInquiryResponse';
import { AsContextAttributes } from '../models/AsContextAttributes';
import { CancelResponse, CancelTransactionDetails } from '../models/CancelTransactionDetails';
import { JsonApiListWrap, JsonApiWrap, NoAttributes } from '../models/JsonApiWrap';
import {
  NicknameOperation,
  NicknameRequest,
  NicknameResponse
} from '../models/Nickname';
import { StatusViewResponse } from '../models/StatusViewResponse';
import { SubmitTaxWithholdingsRequest } from '../models/SubmitTaxWithholdingsRequest';
import { UpdateEmailRequest } from '../models/UpdateEmailRequest';
import { VerifyTaxWithholdingsRequest } from '../models/VerifyTaxWithholdingsRequest';
import { VerifyTaxWithholdingsResponse } from '../models/VerifyTaxWithholdingsResponse';
import { HttpMethods } from '../utilities/Constants';
import { HeadersService } from '../utilities/HeadersService';
import { BaseRestService } from './BaseRestService';
import { IntegrationService } from './integration.service';
import { DraftListAttributes, DraftResponse } from '../../initiate-and-release/Models/Drafts/DraftResponse'
import { MsvTransactionResponse } from '../../create-request/models/master-status-view/TransactionsResponse';
import { ActivityReportResponse } from '../../create-request/models/master-status-view/ActivityReportResponse';
import { MsvLetterAttributes } from '../../create-request/models/Standing-Authorizations';

@Injectable({
  providedIn: 'root',
})
export class StatusViewService  {
  statusViewExperiencePath: string =
    'api/ria.MoneyMovementStatusviewExperience';
  constructor(
    private http: HttpClient,
    private headersService: HeadersService,
    private baseService: BaseRestService,
    private integrationService: IntegrationService
  ) {}

  getAccountOverview(accountId: string, isPalAccount?: boolean): Observable<StatusViewResponse> {
    const headers = this.getHeadersWithClientIds(`1=${accountId}`, isPalAccount);
    headers['Money-Movement-Ui-Origin'] = '1';
    headers['Aggregate-Sloa-Table'] = 'true';
    const url = `${this.integrationService.JfkEnv.BaseUrl}${
      this.statusViewExperiencePath
    }/v1/Overview?${encodeURIComponent(
      'filter[subaccount]'
    )}=${encodeURIComponent('$1')}`;

    return this.baseService.get<StatusViewResponse>(url, headers, true);
  }

  // remove when jfk2 testing is done
  getAccountOverviewJfk2(accountId: string): any {
    const headers = this.getHeadersWithClientIds(`1=${accountId}`);
    const url = `https://jfk2-api-gateway-uat.dev.schwab.com${
      this.statusViewExperiencePath
    }/v1/Overview?${encodeURIComponent(
      'filter[subaccount]'
    )}=${encodeURIComponent('$1')}`;
    const res = this.http
      .get<JsonApiWrap<StatusViewResponse>>(url, { headers })
      .pipe(
        map((res) => res.data.attributes),
        catchError((err) => {
          return of(err);
        })
      )
      .subscribe((res) => res);
  }

  captureAdvisorAgreement(userAgreement: boolean): Observable<any> {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.statusViewExperiencePath +
      '/v1/Agreement';
    let requestBody: JsonApiWrap<any> = {
      data: {
        attributes: {
          userClickedAgree: userAgreement
        },
        type: 'advisor-agreement-request',
        id: ''
      },
    };
    let headers = this.headersService.createHeadersWithJsonApiContentSharedToken();

    return this.baseService.post<any, any>(
      serviceUrl,
      headers,
      requestBody,
      true
    );
  }

  callAchInquiry(
    requestData: AchInquiryRequest
  ): Observable<AchInquiryResponse> {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.statusViewExperiencePath +
      '/v1/MoneyLinkInquiry';
    let requestBody: JsonApiWrap<AchInquiryRequest> = {
      data: {
        attributes: requestData,
        type: 'moneylink-transaction-request',
        id: '' + requestData.cashieringRequestId,
      },
    };
    let headers =
      this.headersService.createHeadersWithJsonApiContentSharedToken('1');
    return this.baseService.post<AchInquiryRequest, AchInquiryResponse>(
      serviceUrl,
      headers,
      requestBody,
      true
    );
  }

  callInternalInquiry(
    requestData: InternalTransferInquiryRequest
  ): Observable<InternalTransferInquiryResponse> {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.statusViewExperiencePath +
      '/v1/JournalInquiry';
    let requestBody: JsonApiWrap<InternalTransferInquiryRequest> = {
      data: {
        attributes: requestData,
        type: 'journal-transaction-request',
        id: '' + requestData.cashieringRequestId,
      },
    };
    let headers =
      this.headersService.createHeadersWithJsonApiContentSharedToken('1');
    return this.baseService.post<
      InternalTransferInquiryRequest,
      InternalTransferInquiryResponse
    >(serviceUrl, headers, requestBody, true);
  }

  callWireInquiry(
    requestData: WireInquiryRequest,
  ): Observable<WireInquiryResponse> {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.statusViewExperiencePath +
      '/v1/WireInquiry';
    let requestBody: JsonApiWrap<WireInquiryRequest> = {
      data: {
        attributes: requestData,
        type: 'wire-transaction-request',
        id: '' + requestData.cashieringRequestId,
      },
    };
    
    let isoResourceVersion = "3";

    let headers =
      this.headersService.createHeadersWithJsonApiContentSharedToken(isoResourceVersion);
    return this.baseService.post<WireInquiryRequest, WireInquiryResponse>(
      serviceUrl,
      headers,
      requestBody,
      true
    );
  }

  callCheckInquiry(
    requestData: CheckInquiryRequest
  ): Observable<CheckInquiryResponse> {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.statusViewExperiencePath +
      '/v1/CheckInquiry';
    let requestBody: JsonApiWrap<CheckInquiryRequest> = {
      data: {
        attributes: requestData,
        type: 'check-transaction-request',
        id: '' + requestData.cashieringRequestId
      },
    };
    let headers =
      this.headersService.createHeadersWithJsonApiContentSharedToken('1');
    return this.baseService.post<CheckInquiryRequest, CheckInquiryResponse>(
      serviceUrl,
      headers,
      requestBody,
      true
    );
  }


  updateEmailAddress(requestData: UpdateEmailRequest): Observable<any> {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.statusViewExperiencePath +
      '/v1/EmailAddress';
    let requestBody: JsonApiWrap<UpdateEmailRequest> = {
      data: {
        attributes: requestData,
        type: 'update-email',
        id: ''
      },
    };
    let headers = this.headersService.createHeadersWithJsonApiContentSharedToken();
    return this.baseService.request<UpdateEmailRequest, any>(
      serviceUrl,
      headers,
      requestBody,
      'PATCH',
      false
    );
  }

  verifyTaxWithholdings(requestData: VerifyTaxWithholdingsRequest, id: string): Observable<any> {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.statusViewExperiencePath +
      '/v1/VerifyTaxWithholdings';
    let requestBody: JsonApiWrap<VerifyTaxWithholdingsRequest> = {
      data: {
        attributes: requestData,
        type: 'verify-tax-withholdings-request',
        id: id
      },
    };
    let headers = this.headersService.createHeadersWithJsonApiContentSharedToken();
    return this.baseService.post<VerifyTaxWithholdingsRequest, VerifyTaxWithholdingsResponse>(
      serviceUrl,
      headers,
      requestBody,
      true
    );
  }

  submitTaxWithholdings(requestData: SubmitTaxWithholdingsRequest, id: string): Observable<any> {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.statusViewExperiencePath +
      '/v1/SubmitTaxWithholdings';
    let requestBody: JsonApiWrap<SubmitTaxWithholdingsRequest> = {
      data: {
        attributes: requestData,
        type: 'submit-tax-withholdings-request',
        id: id
      },
    };
    let headers = this.headersService.createHeadersWithJsonApiContentSharedToken();
    return this.baseService.post<SubmitTaxWithholdingsRequest, any>(
      serviceUrl,
      headers,
      requestBody,
      false
    );
  }

  authorize(requestData: AccessFunctionRequest): Observable<{[key: string]: boolean}> {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.statusViewExperiencePath +
      '/v1/Authorize';
    let headers = this.headersService.createHeadersWithJsonApiContentSharedToken();
    return this.baseService.post<AccessFunctionRequest, {[key: string]: boolean}>(
      serviceUrl,
      headers,
      requestData,
      false
    );
  }

  addEditNickname(requestData: NicknameRequest, requestType: string) {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.statusViewExperiencePath +
      '/v1/Nickname';
    if (requestData.nicknameRecord.nickname === '') {
      requestType = NicknameOperation.Delete;
    }

    let httpMethod: HttpMethods = 'POST';
    if (requestType === NicknameOperation.Edit) {
      httpMethod = 'PUT';
    } else if (requestType === NicknameOperation.Delete) {
      httpMethod = 'DELETE';
    }

    let requestBody: JsonApiWrap<NicknameRequest> = {
      data: {
        attributes: requestData,
        type: 'nickname-request',
        id: '',
      },
    };

    let headers =
      this.headersService.createHeadersWithJsonApiContentSharedToken();
    return this.baseService.request<NicknameRequest, NicknameResponse>(
      serviceUrl,
      headers,
      requestBody,
      httpMethod,
      true
    );
  }

  getContextId(contextRequest: AsContextAttributes): Observable<any>{
    let url = this.integrationService.JfkEnv.BaseUrl + this.statusViewExperiencePath + '/v1/Context';
    let corrId = uuidv4();
    let headers = this.headersService.createHeadersWithJsonApiContentSharedToken(undefined, corrId )
    let requestBody: JsonApiWrap<AsContextAttributes> = {
      data: {
        attributes: contextRequest,
        type: 'context',
        id: corrId
      }
    }

    return this.baseService.post<AsContextAttributes, any>(url, headers, requestBody, false);
  }

  public getHeadersWithClientIds(clientIds: string, isPalAccount?: boolean): {
    [name: string]: string;
  } {
    var headers =
      this.headersService.createHeadersWithJsonApiContentSharedToken(
        '1'
      );
    headers['Schwab-Resource-Version'] = '1';
    headers['Cache-Control'] = 'no-cache';
    headers['Schwab-Client-Ids'] = clientIds;
    if (isPalAccount) {
      headers['Account-Type'] = 'PALACCOUNT';
    }
    // TODO: get this from the context
    // headers['Schwab-Client-FirmId'] = this.datacontext.getFirmId();
    return headers;
  }

  public cancelTransaction(request: CancelTransactionDetails): Observable<CancelResponse> {
    const serviceUrl =
    this.integrationService.JfkEnv.BaseUrl +
    this.statusViewExperiencePath +
    '/v1/Cancel';

    const headers =
      this.headersService.createHeadersWithJsonApiContentSharedToken();

    return this.baseService.request<CancelTransactionDetails, CancelResponse>(
      serviceUrl,
      headers,
      request,
      "POST",
      false
    );
  }

  getInitReleaseTransactions(masterAccount: string, subAccount: string): Observable<DraftResponse> {
    const clientIds = !!subAccount ? `2=${subAccount}` : `1=${masterAccount}`;
    const filterParam = !!subAccount ? `filter[subaccount]=%242` : `filter[master]=%241`;

    const headers = this.getHeadersWithClientIds(clientIds);
    headers['Money-Movement-Ui-Origin'] = '1';
    const url = `${this.integrationService.JfkEnv.BaseUrl}${this.statusViewExperiencePath
      }/v1/initReleasev2?${filterParam}`;
    return this.baseService.get<DraftResponse>(url, headers, false);
  }

  getInitReleaseTransactionByDraftId(draftId: string): Observable<DraftListAttributes> {
    let serviceUrl = this.integrationService.JfkEnv.BaseUrl +
                     this.statusViewExperiencePath +
                     '/v1/initRelease/' + draftId;
    let headers =
      this.headersService.createHeadersWithJsonApiContentSharedToken();

    return this.baseService.get<DraftListAttributes>(serviceUrl, headers, false);
  }

  getMasterAccountTransactions(masterAccount: string, isRecurring: boolean = false, bookmarked: boolean = false): Observable<MsvTransactionResponse> {
    const clientId = `1=${masterAccount}`;
    const masterFilterParam = `filter[master]=%241`;
    const recurringFilterParam = `filter[isRecurring]=` + isRecurring.toString();
    const bookmarkedFilterParam = `filter[isAccountBookmarked]=` + bookmarked.toString();
    const headers = this.getHeadersWithClientIds(clientId);
    headers['Money-Movement-Ui-Origin'] = '1';
    const url = `${this.integrationService.JfkEnv.BaseUrl}${this.statusViewExperiencePath
      }/v1/Transactions?${masterFilterParam}&${recurringFilterParam}&${bookmarkedFilterParam}`;
    return this.baseService.get<MsvTransactionResponse>(url, headers, false);
  }

  getActivityReports(masterAccount: string): Observable<JsonApiListWrap<ActivityReportResponse>> {
    const clientId = `1=${masterAccount}`;
    const headers = this.getHeadersWithClientIds(clientId);
    headers['Money-Movement-Ui-Origin'] = '1';

    const url = `${this.integrationService.JfkEnv.BaseUrl}${this.statusViewExperiencePath}/v1/ActivityReports`
    return this.baseService.get<JsonApiListWrap<ActivityReportResponse>>(url, headers, false).pipe(map((res)=> res));
  }

  getMasterAccountStandingAuthorizations(masterAccount: string, bookmarked: boolean = false) {
    const clientId = `1=${masterAccount}`;
    // TODO: bookmarked accounts retrieval to be done in a future story
    const bookmarkedFilterParam = `filter[isAccountBookmarked]=` + bookmarked.toString();
    const headers = this.getHeadersWithClientIds(clientId);
    headers['Money-Movement-Ui-Origin'] = '1';
    const url = `${this.integrationService.JfkEnv.BaseUrl}${this.statusViewExperiencePath
    }/v1/Letters?${bookmarkedFilterParam}`;
    let requestBody: NoAttributes = {
      data: {
        type: 'LettersQuery',
        id: masterAccount,
      }
    }
    return this.baseService.post<NoAttributes, JsonApiListWrap<MsvLetterAttributes>>(url, headers, requestBody, false)
  }

  downloadActivityReport(masterAccount: string, activityReportId: string, interimSequenceId: string): Observable<Blob> {
    const clientId = `1=${masterAccount}`;
    const headers = this.getHeadersWithClientIds(clientId);
    headers['Money-Movement-Ui-Origin'] = '1';
    headers['Interim-Sequence-Id'] = interimSequenceId
    headers['Master-Account-In-Context'] = masterAccount;
    headers['Schwab-Client-FirmId'] = this.integrationService.currentContext?.firmId?.toString();

    const url = `${this.integrationService.JfkEnv.BaseUrl}${this.statusViewExperiencePath}/v1/ActivityReports/` + activityReportId;
    return this.http.get(url, { headers, responseType: 'blob' });
  }
}
